import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate, fmtTime, STATUS_META } from "@/lib/format";
import {
  Search, Loader2, FileText, UserPlus, Ban, PlusCircle, Filter, Printer, Trash2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const STATUSES = Object.keys(STATUS_META);

export default function Orders() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [assignOrder, setAssignOrder] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["orders", q, status],
    queryFn: async () => (await api.get("/orders", { params: { q: q || undefined, status: status === "all" ? undefined : status } })).data,
  });
  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: async () => (await api.get("/drivers")).data });

  const cancel = (o) => setConfirm({
    title: `Cancel order #${o.order_number}?`,
    description: "This marks the delivery as cancelled. You can still delete it later.",
    confirmLabel: "Cancel Order", danger: true,
    onConfirm: async () => {
      try { await api.post(`/orders/${o.id}/cancel`); qc.invalidateQueries({ queryKey: ["orders"] }); toast.success("Order cancelled"); }
      catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    },
  });

  const del = (o) => setConfirm({
    title: `Delete order #${o.order_number}?`,
    description: "This permanently removes the order and its audit trail. This cannot be undone.",
    confirmLabel: "Delete", danger: true,
    onConfirm: async () => {
      try { await api.delete(`/orders/${o.id}`); qc.invalidateQueries({ queryKey: ["orders"] }); toast.success("Order deleted"); }
      catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    },
  });

  return (
    <div className="animate-slide-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold text-[#0B192C]">Orders</h1>
          <p className="text-sm text-slate-500">{orders.length} orders</p>
        </div>
        <button onClick={() => navigate("/admin/orders/new")} data-testid="create-order-btn" className="flex items-center gap-2 rounded-xl bg-[#FF6500] px-4 py-2.5 text-sm font-bold text-white tap-shrink hover:bg-[#E05A00]">
          <PlusCircle size={18} /> Create Order
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="orders-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search order #, customer, POD, invoice, driver…"
            className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-[#0B192C]"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[200px] bg-white" data-testid="orders-status-filter"><Filter size={15} className="mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white scrollbar-thin">
        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#FF6500]" /></div>
        ) : (
          <table className="w-full text-sm" data-testid="orders-table">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Order</th><th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Driver</th>
                <th className="px-4 py-3">Items</th><th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Arrival</th><th className="px-4 py-3">POD</th><th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No orders found</td></tr>}
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`order-row-${o.order_number}`}>
                  <td className="px-4 py-3 font-mono font-bold text-[#FF6500]">#{o.order_number}</td>
                  <td className="px-4 py-3 font-semibold text-[#0B192C]">{o.customer_snapshot.name}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(o.delivery_date, { day: "numeric", month: "short" })}</td>
                  <td className="px-4 py-3 text-slate-600">{o.driver_name || <span className="text-amber-600">Unassigned</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{o.total_items}</td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-slate-500">{fmtTime(o.delivery?.arrived_at)}</td>
                  <td className="px-4 py-3">
                    {o.pod_number ? (
                      <button onClick={() => navigate(`/admin/pod/${o.id}`)} data-testid={`view-pod-${o.order_number}`} className="flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:underline">
                        <FileText size={14} /> View
                      </button>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => navigate(`/admin/booking/${o.id}`)} data-testid={`booking-${o.order_number}`} title="Print booking slip" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Printer size={16} /></button>
                      {!["Completed", "Cancelled", "Partial Delivery"].includes(o.status) && (
                        <button onClick={() => setAssignOrder(o)} data-testid={`assign-${o.order_number}`} title="Assign driver" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><UserPlus size={16} /></button>
                      )}
                      {!["Completed", "Cancelled", "Partial Delivery", "Failed Delivery"].includes(o.status) && (
                        <button onClick={() => cancel(o)} data-testid={`cancel-${o.order_number}`} title="Cancel" className="rounded-lg p-1.5 text-amber-600 hover:bg-amber-50"><Ban size={16} /></button>
                      )}
                      <button onClick={() => del(o)} data-testid={`delete-${o.order_number}`} title="Delete" className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AssignDialog order={assignOrder} drivers={drivers} onClose={() => setAssignOrder(null)} onDone={() => { setAssignOrder(null); qc.invalidateQueries({ queryKey: ["orders"] }); }} />
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function AssignDialog({ order, drivers, onClose, onDone }) {
  const [driverId, setDriverId] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [saving, setSaving] = useState(false);
  React.useEffect(() => { setDriverId(order?.driver_id || ""); setVehicle(order?.vehicle || ""); }, [order]);

  const save = async () => {
    if (!driverId) return toast.error("Select a driver");
    setSaving(true);
    try {
      await api.post(`/orders/${order.id}/assign`, { driver_id: driverId, vehicle });
      toast.success("Delivery assigned & driver notified");
      onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="assign-dialog">
        <DialogHeader><DialogTitle>Assign Delivery {order && `· #${order.order_number}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Driver</label>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger className="mt-1" data-testid="assign-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
              <SelectContent>
                {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} {d.vehicle ? `· ${d.vehicle}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vehicle (optional)</label>
            <input value={vehicle} onChange={(e) => setVehicle(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#0B192C]" placeholder="e.g. Isuzu 14T" />
          </div>
          <button onClick={save} disabled={saving} data-testid="assign-confirm-btn" className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B192C] py-3 font-bold text-white tap-shrink disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Assign Delivery"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
